import {
  Alert,
  createIcon,
  Box,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import NumberFormat from "react-number-format";
import { amountFormat, percentageFormat } from "../constants";
import PieSectionContainer from "./PieSectionContainer";
import TwoCol from "./TwoCol";

export interface ResultsProps {
  amount?: number;
  totalDisposablePortion: number;
  sharesPieData: Array<{
    id: string;
    label: string;
    value: number;
  }>;
  portionsPieData: Array<{
    id: string;
    label: string;
    value: number;
  }>;
  optionalInheritors: string[];
}

export const InfoCircleIcon = createIcon({
  displayName: "InfoCircleIcon",
  viewBox: "0 0 24 24",
  defaultProps: {
    boxSize: "24px", // замінює width/height
    color: "#336CF7", // дефолтний колір, але можна переписати через color проп
  },
  path: (
    <>
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="16"
        x2="12"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="12"
        y1="8"
        x2="12.01"
        y2="8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
});

const Results: React.FC<ResultsProps> = ({
  amount,
  totalDisposablePortion,
  sharesPieData,
  portionsPieData,
  optionalInheritors,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <TwoCol
        col1Props={{ flexGrow: 1, display: "flex", flexDir: "column" }}
        col2Props={{ flexGrow: 1, display: "flex", flexDir: "column" }}
        col1={
          <>
            {/* Intro */}
            <VStack spacing={3} alignItems="flex-start">
              <Heading as="h3" textStyle="h3">
                {t("heading_statutory_portions")}
              </Heading>
              <Text textStyle="paragraph-sm">
                {t("text_statutory_portions")}
              </Text>
            </VStack>

            {/* Chart */}
            <Box py={{ base: 8, lg: 16, xl: 20 }} mt="auto">
              <PieSectionContainer pieData={sharesPieData} />
            </Box>
          </>
        }
        col2={
          <>
            {/* Intro */}
            <VStack spacing={3} alignItems="flex-start">
              <Heading as="h3" textStyle="h3">
                {t("heading_disposable_portions")}
              </Heading>
              <Text textStyle="paragraph-sm">
                {t("text_disposable_portions")}
              </Text>
            </VStack>

            {/* Chart */}
            <Box py={{ base: 8, lg: 16, xl: 20 }} mt="auto">
              <PieSectionContainer pieData={portionsPieData} />
            </Box>
          </>
        }
      />

      {/* Text */}
      {!!optionalInheritors.length && (
        <TwoCol
          stackProps={{
            mt: { base: -4, sm: 0, lg: -16 / 2, xl: -20 / 2 },
            pb: { base: 4, lg: 16, xl: 20 },
            spacing: {
              base: 0,
              lg: 12,
              xl: 16,
            },
          }}
          col1={null}
          col2={
            <Alert
              status="info"
              colorScheme="gray"
              bgColor={"blue.550"}
              alignItems="flex-start"
            >
              <InfoCircleIcon mr="5px" />
              <Text textStyle="paragraph-sm">
                <Trans
                  i18nKey="disposable_portion_text"
                  components={[
                    // Disposable portion percentage
                    <NumberFormat
                      value={totalDisposablePortion * 100}
                      {...percentageFormat}
                    />,

                    // Disposable portion amount (optional)
                    ...(!!amount
                      ? [
                          <NumberFormat
                            prefix=" / "
                            value={totalDisposablePortion * amount}
                            displayType="text"
                            {...amountFormat(
                              totalDisposablePortion * amount ?? 0
                            )}
                          />,
                        ]
                      : [<span />]),

                    // Disposable portion inheritors
                    <span
                      dangerouslySetInnerHTML={{
                        __html: optionalInheritors.join(", "),
                      }}
                    />,
                  ]}
                />
              </Text>
            </Alert>
          }
        />
      )}
    </>
  );
};

export default Results;
